import { Icon } from '@gftg/ui';
import { formatDateRange } from '../lib/datetime';
import type { PublicEvent } from '../lib/queries';

interface Chip {
  label: string;
  href: string;
  external: boolean;
}

/**
 * The client's own destinations live in `events.links`, so the footer follows whatever the
 * organizer has filled in: an empty link is simply absent rather than a dead chip.
 */
export function SiteFooter({ event }: { event: PublicEvent | undefined }) {
  if (!event) {
    return null;
  }

  const { links } = event;
  const chips: Chip[] = [
    ...(event.contactEmail
      ? [{ label: 'Contact the team', href: `mailto:${event.contactEmail}`, external: false }]
      : []),
    ...(links.donate ? [{ label: 'Donate', href: links.donate, external: true }] : []),
    ...(links.store ? [{ label: 'GFTG store', href: links.store, external: true }] : []),
    ...(links.privacy ? [{ label: 'Privacy', href: links.privacy, external: false }] : []),
    ...(links.website
      ? [{ label: hostLabel(links.website), href: links.website, external: true }]
      : []),
  ];

  const place = [event.venueName, event.venueCity].filter(Boolean).join(', ');

  return (
    <footer className="px-gutter border-t border-border-3 bg-sand pt-6 pb-8">
      <p className="font-display text-card-title font-semibold text-ink">{event.name}</p>
      <p className="text-subtitle mt-1 text-ink-3">
        {formatDateRange(event.startsOn, event.endsOn, event.timezone)}
        {place && ` · ${place}`}
      </p>

      {chips.length > 0 && (
        <ul className="mt-4 flex flex-wrap gap-2">
          {chips.map((chip) => (
            <li key={chip.label}>
              <a
                href={chip.href}
                {...(chip.external ? { target: '_blank', rel: 'noreferrer noopener' } : {})}
                className="text-chip inline-flex min-h-[30px] items-center gap-1.5 rounded-full border border-border bg-surface px-3.5 font-semibold text-ink-2"
              >
                {chip.label}
                {chip.external && <Icon name="external" size={12} className="text-hairline" />}
              </a>
            </li>
          ))}
        </ul>
      )}

      {/* Discreet attribution, as the brief asks for (module 9). */}
      <p className="text-eyebrow mt-6 flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-border-3 pt-4 text-ink-4">
        <span className="size-2 shrink-0 rounded-full bg-brand" aria-hidden="true" />
        Built and hosted by ApolloRise
        <span className="ml-auto tabular-nums">v{import.meta.env.VITE_APP_VERSION}</span>
      </p>
    </footer>
  );
}

/** "https://goforthegreens.org/" reads as a link; "goforthegreens.org" reads as the client. */
function hostLabel(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}
